import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import GetUserDemographics from "../queries/GetUserDemographics";
import Select from "react-select";
import Spinner from "./Spinner";
import writeXlsxFile from "write-excel-file";
import { useAppContext } from "../AppContext";
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Row,
  Col,
  FormGroup,
  Button,
} from "reactstrap";

const Platforms = () => {
  const { error, loading, data, refetch } = useQuery(GetUserDemographics, {
    // fetchPolicy: "network-only",
    variables: {
      survey_language: null,
      trial_type: null,
      survey_type: null,
    },
  });
  const [selectedLanguage, setSelectedLanguage] = useState({
    value: null,
    label: "None",
  });
  const [selectedTrialType, setSelectedTrialType] = useState({
    value: null,
    label: "None",
  });
  const [selectedSurveyType, setSelectedSurveyType] = useState({
    value: null,
    label: "None",
  });
  const users = useRef([]);
  const { displayToast, closeToast } = useAppContext();
  const platforms = [
    "pc_edge",
    "pc_chrome",
    "pc_firefox",
    "pc_other",
    "mac_safari",
    "mac_chrome",
    "mac_firefox",
    "mac_other",
    "ios_tablet",
    "ios_smartphone",
    "ios_safari",
    "ios_edge",
    "ios_chrome",
    "ios_firefox",
    "ios_other",
    "android_tablet",
    "android_smartphone",
    "android_edge",
    "android_chrome",
    "android_firefox",
    "android_other",
    "vpn",
  ];
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const runRefetch = async () => {
      let variables;

      if (
        (!selectedLanguage || selectedLanguage.value === null) &&
        (!selectedTrialType || selectedTrialType.value === null) &&
        (!selectedSurveyType || selectedSurveyType.value === null)
      ) {
        if (users.current.length === 0) return;

        variables = {
          survey_language: null,
          trial_type: null,
          survey_type: null,
        };
      } else {
        variables = {
          survey_language: selectedLanguage?.value ?? null,
          trial_type: selectedTrialType?.value ?? null,
          survey_type: selectedSurveyType?.value ?? null,
        };
      }

      setIsFetching(true);

      try {
        await refetch(variables);
      } finally {
        setIsFetching(false);
      }
    };

    runRefetch();
  }, [selectedLanguage, selectedTrialType, selectedSurveyType, refetch]);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  // Assuming `data.users` is an array of user objects
  users.current = data.users.filter(
    (user) =>
      user.surveys && Array.isArray(user.surveys) && user.surveys.length > 0,
  );

  console.log("users: ", users.current);

  function calculatePercentage(numerator, denominator) {
    return denominator > 0 ? numerator / denominator : 0;
  }

  function generateExcel() {
    let success = users.current !== null && users.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    console.log("users: ", users.current);

    // the following three variables will be filled while user data is added to the table, this will be used for the second table to calculate percentages
    const count_by_platform = {
      pc: 0,
      mac: 0,
      ios: 0,
      android: 0,
    };
    const count_mobile_dev_by_op_sys = {
      smartphone: {
        ios: 0,
        android: 0,
      },
      tablet: {
        ios: 0,
        android: 0,
      },
    };
    const count_web_browser_by_op_sys = {
      edge: {
        pc: 0,
        mac: null,
        ios: 0,
        android: 0,
      },
      chrome: {
        pc: 0,
        mac: 0,
        ios: 0,
        android: 0,
      },
      firefox: {
        pc: 0,
        mac: 0,
        ios: 0,
        android: 0,
      },
      safari: {
        pc: null,
        mac: 0,
        ios: 0,
        android: null,
      },
      other: {
        pc: 0,
        mac: 0,
        ios: 0,
        android: 0,
      },
    };

    let tableData = [];

    // sets header for user data in table
    tableData.push([
      {
        type: String,
        value: "User ID",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Platforms Used",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
    ]);
    // adds all the user data to table
    users.current.forEach((user, uIndex) => {
      const usedPlatforms = platforms.reduce((acc, platform) => {
        acc[platform] = null; //initialize each key with null
        return acc;
      }, {});

      for (let platform of platforms) {
        for (let survey of user.surveys) {
          if (survey[platform] === true) {
            usedPlatforms[platform] = true;
            break;
          } else if (
            usedPlatforms[platform] !== true &&
            survey[platform] !== null
          ) {
            usedPlatforms[platform] = false;
          }
        }
      }

      let dataToDisplay = {
        pc: [],
        mac: [],
        ios: [],
        android: [],
        vpn: false,
      };
      for (let platform of platforms) {
        // If the platform is marked as true, or if it has been considered but not marked as false (thus not null or false)
        // it implies there's an interest or usage of that platform.
        if (
          usedPlatforms[platform] !== null &&
          usedPlatforms[platform] !== false
        ) {
          switch (platform) {
            case "pc_edge":
              // Logic for pc_edge
              dataToDisplay.pc.push("Edge");
              count_by_platform.pc++;
              count_web_browser_by_op_sys.edge.pc++;
              break;
            case "pc_chrome":
              // Logic for pc_chrome
              dataToDisplay.pc.push("Chrome");
              count_by_platform.pc++;
              count_web_browser_by_op_sys.chrome.pc++;
              break;
            case "pc_firefox":
              // Logic for pc_firefox
              dataToDisplay.pc.push("Firefox");
              count_by_platform.pc++;
              count_web_browser_by_op_sys.firefox.pc++;
              break;
            case "pc_other":
              // Logic for pc_other
              dataToDisplay.pc.push("Other");
              count_by_platform.pc++;
              count_web_browser_by_op_sys.other.pc++;
              break;
            case "mac_safari":
              // Logic for mac_safari
              dataToDisplay.mac.push("Safari");
              count_by_platform.mac++;
              count_web_browser_by_op_sys.safari.mac++;
              break;
            case "mac_chrome":
              // Logic for mac_chrome
              dataToDisplay.mac.push("Chrome");
              count_by_platform.mac++;
              count_web_browser_by_op_sys.chrome.mac++;
              break;
            case "mac_firefox":
              // Logic for mac_firefox
              dataToDisplay.mac.push("Firefox");
              count_by_platform.mac++;
              count_web_browser_by_op_sys.firefox.mac++;
              break;
            case "mac_other":
              // Logic for mac_other
              dataToDisplay.mac.push("Other");
              count_by_platform.mac++;
              count_web_browser_by_op_sys.other.mac++;
              break;
            case "ios_tablet":
              // Logic for ios_tablet
              dataToDisplay.ios.push("Tablet");
              count_by_platform.ios++;
              count_mobile_dev_by_op_sys.tablet.ios++;
              break;
            case "ios_smartphone":
              // Logic for ios_smartphone
              dataToDisplay.ios.push("Smartphone");
              count_by_platform.ios++;
              count_mobile_dev_by_op_sys.smartphone.ios++;
              break;
            case "ios_safari":
              // Logic for ios_safari
              dataToDisplay.ios.push("Safari");
              count_by_platform.ios++;
              count_web_browser_by_op_sys.safari.ios++;
              break;
            case "ios_edge":
              // Logic for ios_edge
              dataToDisplay.ios.push("Edge");
              count_by_platform.ios++;
              count_web_browser_by_op_sys.edge.ios++;
              break;
            case "ios_chrome":
              // Logic for ios_chrome
              dataToDisplay.ios.push("Chrome");
              count_by_platform.ios++;
              count_web_browser_by_op_sys.chrome.ios++;
              break;
            case "ios_firefox":
              // Logic for ios_firefox
              dataToDisplay.ios.push("Firefox");
              count_by_platform.ios++;
              count_web_browser_by_op_sys.firefox.ios++;
              break;
            case "ios_other":
              // Logic for ios_other
              dataToDisplay.ios.push("Other");
              count_by_platform.ios++;
              count_web_browser_by_op_sys.other.ios++;
              break;
            case "android_tablet":
              // Logic for android_tablet
              dataToDisplay.android.push("Tablet");
              count_by_platform.android++;
              count_mobile_dev_by_op_sys.tablet.android++;
              break;
            case "android_smartphone":
              // Logic for android_smartphone
              dataToDisplay.android.push("Smartphone");
              count_by_platform.android++;
              count_mobile_dev_by_op_sys.tablet.smartphone++;
              break;
            case "android_edge":
              // Logic for android_edge
              dataToDisplay.android.push("Edge");
              count_by_platform.android++;
              count_web_browser_by_op_sys.edge.android++;
              break;
            case "android_chrome":
              // Logic for android_chrome
              dataToDisplay.android.push("Chrome");
              count_by_platform.android++;
              count_web_browser_by_op_sys.chrome.android++;
              break;
            case "android_firefox":
              // Logic for android_firefox
              dataToDisplay.android.push("Firefox");
              count_by_platform.android++;
              count_web_browser_by_op_sys.firefox.android++;
              break;
            case "android_other":
              // Logic for android_other
              dataToDisplay.android.push("Other");
              count_by_platform.android++;
              count_web_browser_by_op_sys.other.android++;
              break;
            case "vpn":
              // Logic for vpn
              dataToDisplay.vpn = true;
              break;
            default:
              // dataToDisplay.push("Platform not recognized");
              break;
          }
        }
      }

      // following builds out the text that'll be put into each users cell for their platforms seperated by || so that later the user can tell it to do a next line with a find replace all
      let platformTextForExcel = "";
      if (dataToDisplay.pc.length > 0) {
        if (platformTextForExcel !== "") platformTextForExcel += "||";

        platformTextForExcel += "PC: ";
        dataToDisplay.pc.forEach((browser, bIndex) => {
          if (bIndex > 0) {
            platformTextForExcel += ", ";
          }
          platformTextForExcel += browser;
        });
      }
      if (dataToDisplay.mac.length > 0) {
        if (platformTextForExcel !== "") platformTextForExcel += "||";

        platformTextForExcel += "Mac: ";
        dataToDisplay.mac.forEach((browser, bIndex) => {
          if (bIndex > 0) {
            platformTextForExcel += ", ";
          }
          platformTextForExcel += browser;
        });
      }
      if (dataToDisplay.ios.length > 0) {
        if (platformTextForExcel !== "") platformTextForExcel += "||";

        platformTextForExcel += "iOS: ";
        dataToDisplay.ios.forEach((browser, bIndex) => {
          if (bIndex > 0) {
            platformTextForExcel += ", ";
          }
          platformTextForExcel += browser;
        });
      }
      if (dataToDisplay.android.length > 0) {
        if (platformTextForExcel !== "") platformTextForExcel += "||";

        platformTextForExcel += "Android: ";
        dataToDisplay.android.forEach((browser, bIndex) => {
          if (bIndex > 0) {
            platformTextForExcel += ", ";
          }
          platformTextForExcel += browser;
        });
      }
      if (dataToDisplay.vpn) {
        if (platformTextForExcel !== "") platformTextForExcel += "||";

        platformTextForExcel += "VPN was used";
      }

      tableData.push([
        {
          type: String,
          value: user.user_name,
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: platformTextForExcel,
          align: "center",
          width: 12,
        },
      ]);
    });

    console.log(count_by_platform);
    console.log(count_mobile_dev_by_op_sys);
    console.log(count_web_browser_by_op_sys);

    // add space between the two tables
    tableData.push([]);

    // add header for % by platform
    tableData.push([
      {
        type: String,
        value: "Percentages by Platform",
        align: "center",
        width: 24,
        fontWeight: "bold",
        backgroundColor: "#a8aabd",
        span: 5,
      },
      null,
      null,
      null,
      null,
    ]);

    const total_platforms =
      count_by_platform.pc +
      count_by_platform.mac +
      count_by_platform.ios +
      count_by_platform.android;
    // calculate scores here and add data for each of the platforms
    tableData.push([
      {
        type: String,
        value: "Percentage viewed on PC",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(count_by_platform.pc, total_platforms),
        align: "center",
        span: 4,
      },
      null,
      null,
      null,
    ]);
    tableData.push([
      {
        type: String,
        value: "Percentage viewed on Mac",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(count_by_platform.mac, total_platforms),
        align: "center",
        span: 4,
      },
      null,
      null,
      null,
    ]);
    tableData.push([
      {
        type: String,
        value: "Percentage viewed on iOS",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(count_by_platform.ios, total_platforms),
        align: "center",
        span: 4,
      },
      null,
      null,
      null,
    ]);
    tableData.push([
      {
        type: String,
        value: "Percentage viewed on Android",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(count_by_platform.android, total_platforms),
        align: "center",
        span: 4,
      },
      null,
      null,
      null,
    ]);

    // add next header
    tableData.push([
      {
        type: String,
        value: "Mobile Devices by Operating System",
        align: "center",
        fontWeight: "bold",
        backgroundColor: "#a8aabd",
        span: 5,
      },
      null,
      null,
      null,
      null,
    ]);
    // add subheader
    tableData.push([
      null,
      {
        type: String,
        value: "iOS",
        align: "center",
        fontWeight: "bold",
        span: 2,
      },
      null,
      {
        type: String,
        value: "Android",
        align: "center",
        fontWeight: "bold",
        span: 2,
      },
      null,
    ]);
    const total_smartphone =
      count_mobile_dev_by_op_sys.smartphone.ios +
      count_mobile_dev_by_op_sys.smartphone.android;
    const total_tablet =
      count_mobile_dev_by_op_sys.tablet.ios +
      count_mobile_dev_by_op_sys.tablet.android;
    // calculate and add for each mobile device
    tableData.push([
      {
        type: String,
        value: "Smartphone",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_mobile_dev_by_op_sys.smartphone.ios / total_smartphone,
        ),
        align: "center",
        span: 2,
      },
      null,
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_mobile_dev_by_op_sys.smartphone.android / total_smartphone,
        ),
        align: "center",
        span: 2,
      },
      null,
    ]);
    tableData.push([
      {
        type: String,
        value: "Tablet",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_mobile_dev_by_op_sys.tablet.ios,
          total_tablet,
        ),
        align: "center",
        span: 2,
      },
      null,
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_mobile_dev_by_op_sys.tablet.android,
          total_tablet,
        ),
        align: "center",
        span: 2,
      },
      null,
    ]);

    // add final header
    tableData.push([
      {
        type: String,
        value: "Web Browser by Operating System",
        align: "center",
        fontWeight: "bold",
        backgroundColor: "#a8aabd",
        span: 5,
      },
      null,
      null,
      null,
      null,
    ]);
    // add final subheader
    tableData.push([
      null,
      {
        type: String,
        value: "PC",
        align: "center",
        fontWeight: "bold",
      },
      {
        type: String,
        value: "Mac",
        align: "center",
        fontWeight: "bold",
      },
      {
        type: String,
        value: "iOS",
        align: "center",
        fontWeight: "bold",
      },
      {
        type: String,
        value: "Android",
        align: "center",
        fontWeight: "bold",
      },
    ]);
    //calculate and add data for each browser on operating system and leave gaps for any where that browser isn't even available
    tableData.push([
      {
        type: String,
        value: "Microsoft Edge",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.edge.pc,
          count_by_platform.pc,
        ),
        align: "center",
      },
      {
        type: String,
        value: "",
        align: "center",
        backgroundColor: "#a8aabd",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.edge.ios,
          count_by_platform.ios,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.edge.android,
          count_by_platform.android,
        ),
        align: "center",
      },
    ]);
    tableData.push([
      {
        type: String,
        value: "Google Chrome",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.chrome.pc,
          count_by_platform.pc,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.chrome.mac,
          count_by_platform.mac,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.chrome.ios,
          count_by_platform.ios,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.chrome.android,
          count_by_platform.android,
        ),
        align: "center",
      },
    ]);
    tableData.push([
      {
        type: String,
        value: "Mozilla Firefox",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.firefox.pc,
          count_by_platform.pc,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.firefox.mac,
          count_by_platform.mac,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.firefox.ios,
          count_by_platform.ios,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.firefox.android,
          count_by_platform.android,
        ),
        align: "center",
      },
    ]);
    tableData.push([
      {
        type: String,
        value: "Safari",
        align: "left",
      },
      {
        type: String,
        value: "",
        align: "center",
        backgroundColor: "#a8aabd",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.safari.mac,
          count_by_platform.mac,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.safari.ios,
          count_by_platform.ios,
        ),
        align: "center",
      },
      {
        type: String,
        value: "",
        align: "center",
        backgroundColor: "#a8aabd",
      },
    ]);
    tableData.push([
      {
        type: String,
        value: "Other",
        align: "left",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.other.pc,
          count_by_platform.pc,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.other.mac,
          count_by_platform.mac,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.other.ios,
          count_by_platform.ios,
        ),
        align: "center",
      },
      {
        type: Number,
        format: "0.0%",
        value: calculatePercentage(
          count_web_browser_by_op_sys.other.android,
          count_by_platform.android,
        ),
        align: "center",
      },
    ]);

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `platforms_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  function displayPlatforms(user) {
    const usedPlatforms = platforms.reduce((acc, platform) => {
      acc[platform] = null; //initialize each key with null
      return acc;
    }, {});

    for (let platform of platforms) {
      for (let survey of user.surveys) {
        if (survey[platform] === true) {
          usedPlatforms[platform] = true;
          break;
        } else if (
          usedPlatforms[platform] !== true &&
          survey[platform] !== null
        ) {
          usedPlatforms[platform] = false;
        }
      }
    }
    console.log("user.user_name: ", user.user_name);

    let dataToDisplay = {
      pc: [],
      mac: [],
      ios: [],
      android: [],
      vpn: false,
    };
    for (let platform of platforms) {
      // If the platform is marked as true, or if it has been considered but not marked as false (thus not null or false)
      // it implies there's an interest or usage of that platform.
      if (
        usedPlatforms[platform] !== null &&
        usedPlatforms[platform] !== false
      ) {
        switch (platform) {
          case "pc_edge":
            // Logic for pc_edge
            dataToDisplay.pc.push("Edge");
            break;
          case "pc_chrome":
            // Logic for pc_chrome
            dataToDisplay.pc.push("Chrome");
            break;
          case "pc_firefox":
            // Logic for pc_firefox
            dataToDisplay.pc.push("Firefox");
            break;
          case "pc_other":
            // Logic for pc_other
            dataToDisplay.pc.push("Other");
            break;
          case "mac_safari":
            // Logic for mac_safari
            dataToDisplay.mac.push("Safari");
            break;
          case "mac_chrome":
            // Logic for mac_chrome
            dataToDisplay.mac.push("Chrome");
            break;
          case "mac_firefox":
            // Logic for mac_firefox
            dataToDisplay.mac.push("Firefox");
            break;
          case "mac_other":
            // Logic for mac_other
            dataToDisplay.mac.push("Other");
            break;
          case "ios_tablet":
            // Logic for ios_tablet
            dataToDisplay.ios.push("Tablet");
            break;
          case "ios_smartphone":
            // Logic for ios_smartphone
            dataToDisplay.ios.push("Smartphone");
            break;
          case "ios_safari":
            // Logic for ios_safari
            dataToDisplay.ios.push("Safari");
            break;
          case "ios_edge":
            // Logic for ios_edge
            dataToDisplay.ios.push("Edge");
            break;
          case "ios_chrome":
            // Logic for ios_chrome
            dataToDisplay.ios.push("Chrome");
            break;
          case "ios_firefox":
            // Logic for ios_firefox
            dataToDisplay.ios.push("Firefox");
            break;
          case "ios_other":
            // Logic for ios_other
            dataToDisplay.ios.push("Other");
            break;
          case "android_tablet":
            // Logic for android_tablet
            dataToDisplay.android.push("Tablet");
            break;
          case "android_smartphone":
            // Logic for android_smartphone
            dataToDisplay.android.push("Smartphone");
            break;
          case "android_edge":
            // Logic for android_edge
            dataToDisplay.android.push("Edge");
            break;
          case "android_chrome":
            // Logic for android_chrome
            dataToDisplay.android.push("Chrome");
            break;
          case "android_firefox":
            // Logic for android_firefox
            dataToDisplay.android.push("Firefox");
            break;
          case "android_other":
            // Logic for android_other
            dataToDisplay.android.push("Other");
            break;
          case "vpn":
            // Logic for vpn
            dataToDisplay.vpn = true;
            break;
          default:
            // dataToDisplay.push("Platform not recognized");
            break;
        }
      }
    }

    if (
      dataToDisplay.pc.length === 0 &&
      dataToDisplay.mac.length === 0 &&
      dataToDisplay.ios.length === 0 &&
      dataToDisplay.android.length === 0
    ) {
      if (!dataToDisplay.vpn) return "No platforms reported";

      return "Used VPN but did not report specific platforms.";
    } else {
      return (
        <>
          {dataToDisplay.pc.length > 0 && (
            <div style={{ borderBottom: "0.5px solid lightgrey" }}>
              <strong>PC: </strong>
              {dataToDisplay.pc.map((browser, bIndex) => {
                return (
                  <React.Fragment key={`pc_browser_${bIndex}`}>
                    {bIndex > 0 ? `, ${browser}` : browser}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {dataToDisplay.mac.length > 0 && (
            <div style={{ borderBottom: "0.5px solid lightgrey" }}>
              <strong>Mac: </strong>
              {dataToDisplay.mac.map((browser, bIndex) => {
                return (
                  <React.Fragment key={`mac_browser_${bIndex}`}>
                    {bIndex > 0 ? `, ${browser}` : browser}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {dataToDisplay.ios.length > 0 && (
            <div style={{ borderBottom: "0.5px solid lightgrey" }}>
              <strong>iOS: </strong>
              {dataToDisplay.ios.map((browser, bIndex) => {
                return (
                  <React.Fragment key={`ios_browser_${bIndex}`}>
                    {bIndex > 0 ? `, ${browser}` : browser}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {dataToDisplay.android.length > 0 && (
            <div style={{ borderBottom: "0.5px solid lightgrey" }}>
              <strong>Android: </strong>
              {dataToDisplay.android.map((browser, bIndex) => {
                return (
                  <React.Fragment key={`android_browser_${bIndex}`}>
                    {bIndex > 0 ? `, ${browser}` : browser}
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {dataToDisplay.vpn && <div>VPN was used</div>}
        </>
      );
    }
  }

  function displayUsers() {
    return users.current.map((user, uIndex) => {
      if (user.role === "admin") return null;

      return (
        <tr key={user.user_name}>
          <td>{user.user_name}</td>
          <td
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {displayPlatforms(user)}
          </td>
        </tr>
      );
    });
  }

  return (
    <>
      <Button color="primary" onClick={() => generateExcel()}>
        Create Excel
      </Button>
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-language">
                  Language
                </label>
                <Select
                  value={selectedLanguage}
                  onChange={(selectedLanguage) => {
                    setSelectedLanguage(selectedLanguage);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "arabic",
                      label: "Arabic",
                    },
                    {
                      value: "korean",
                      label: "Korean",
                    },
                    {
                      value: "russian",
                      label: "Russian",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-language"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-trial">
                  Trial Type
                </label>
                <Select
                  value={selectedTrialType}
                  onChange={(selectedTrialType) => {
                    setSelectedTrialType(selectedTrialType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "individual",
                      label: "Individual",
                    },
                    {
                      value: "group",
                      label: "Group",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-trial"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {users.current !== null && users.current.length > 0 ? (
            <>
              <div className="d-flex justify-content-center py-2 w-100 font-weight-bold table-dark">
                Course II
              </div>
              <Table>
                <thead>
                  <th>USER ID</th>
                  <th>Platforms Used</th>
                </thead>
                {displayUsers()}
              </Table>
            </>
          ) : isFetching ? (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <Spinner />
              <h3 className="text-muted mt-3">Loading results...</h3>
            </div>
          ) : (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <h1>
                <i className="ni ni-bell-55 text-muted" />
              </h1>
              <h3 className="text-muted">
                Please select what you'd like to view
              </h3>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default Platforms;
